/**
 * Runtime settings of the web app.
 *
 * The app reads its settings from the API at startup (`GET /api/config`) instead of having them
 * built in. The same build therefore runs on every environment (local, staging, production), and
 * the API and the app can never disagree about the chain.
 */

import { createContext, type ReactNode, useContext } from "react";

import { api } from "./api/client.js";

/** Settings the API publishes for the web app. */
export interface AppConfig {
  /** Chain the app works on: 8453 Base, 84532 Base Sepolia, 31337 a local Anvil node. */
  chainId: number;

  /** The platforms whose accounts creators can link. None when absent. */
  linkablePlatforms?: readonly ("X" | "YOUTUBE")[];

  /**
   * The length of a campaign day in seconds, which every campaign deadline counts in. A real day
   * when absent. A test network may shorten it so that a whole campaign runs in about an hour.
   */
  campaignDaySeconds?: number;

  /** Where people write to Morcast. The app offers no contact when absent. */
  supportEmail?: string | null;

  /** Where the app reports browser errors. Nowhere when absent. */
  errorReporting?: ErrorReporting | null;
}

/** Sentry, for browser errors: its DSN, and the deployment and version the reports name. */
export interface ErrorReporting {
  dsn: string;
  environment: string;
  release: string | null;
}

/** Fetches the settings from the API. */
export function loadAppConfig(): Promise<AppConfig> {
  return api.get<AppConfig>("/api/config");
}

const AppConfigContext = createContext<AppConfig | null>(null);

/** Makes the settings available to every component below it. */
export function AppConfigProvider({
  config,
  children,
}: {
  config: AppConfig;
  children: ReactNode;
}) {
  return <AppConfigContext.Provider value={config}>{children}</AppConfigContext.Provider>;
}

/** The app's settings. Must be used below `AppConfigProvider`. */
export function useAppConfig(): AppConfig {
  const config = useContext(AppConfigContext);
  if (!config) {
    throw new Error("useAppConfig must be used inside AppConfigProvider");
  }
  return config;
}
